/**
 * Resolve the address-verification verdict for an explicit Save click.
 *
 * Mirrors {@link useAddressAutocomplete}'s group-blur resolution: accept the
 * top candidate immediately if one is already in hand; otherwise wait out an
 * in-flight Smarty fetch (polling up to ~3s) and re-resolve. A Save click reads
 * as acceptance of the shadow-text candidate, so it passes
 * `acceptTopCandidate=true` — no second Smarty roundtrip. When candidates came
 * back but none completes the typed street (no shadow ever rendered), there's
 * nothing to accept, so resolution returns `{ kind: "unverifiable" }` and the
 * address settles unverified (the caller should surface the unverifiable
 * callout, not treat it as a hard error). If the fetch is still in flight when
 * the poll window expires, the final re-resolve forces a settle (third arg) so
 * the verdict is never `deferred` — the Save POSTs the typed address unverified
 * rather than hanging. Always yields a macrotask at the end so the autofill's
 * onChange writes commit into the surface's state before it reads them to POST.
 *
 * Runs primarily for its side effect (the hook autofills from the accepted
 * candidate); the resolved verdict is returned for callers that need it.
 *
 * Takes refs (not values) because both the resolver and the in-flight flag are
 * read fresh on each poll iteration as the awaited fetch settles.
 *
 * A committed-but-unfilled unit slot ("…Unit _") needs no special flag here: the
 * resolver strips the sentinel and settles the address unverified on its own
 * when it sees the slot in value.addr1.
 *
 * @param {{ current: (isExplicit: boolean, acceptTopCandidate: boolean, forceSettle?: boolean) => { kind: string } }} resolveOnGroupBlurRef
 * @param {{ current: boolean }} fetchInFlightRef
 * @returns {Promise<{ kind: string }>}
 */
// Poll budget while a Smarty fetch is in flight: 150 × 20ms ≈ 3s. The window must
// comfortably exceed debounce (300ms) + round-trip so a fast Enter/Save doesn't settle
// against not-yet-arrived data and save the address unverified. Smarty latency past 3s
// reads as degraded/down: stop waiting and settle unverified rather than hang the Save.
const MAX_WAITS = 150;
const POLL_MS = 20;

export default async function awaitVerificationVerdict(
  resolveOnGroupBlurRef,
  fetchInFlightRef
) {
  let verdict = resolveOnGroupBlurRef.current(false, true);
  if (verdict.kind === "deferred") {
    let waits = 0;
    while (fetchInFlightRef.current && waits < MAX_WAITS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      waits += 1;
    }
    // Re-resolve against the now-settled fetch. If it is STILL in flight (Smarty
    // slow/down past the poll window), force a settle so the Save never hangs:
    // the verdict lands unverifiable and the surface POSTs the typed address
    // unverified, the agreed fallback — rather than holding the editor open.
    const forceSettle = fetchInFlightRef.current;
    verdict = resolveOnGroupBlurRef.current(false, true, forceSettle);
  }
  await new Promise((resolve) => setTimeout(resolve, 0));
  return verdict;
}
